import React from 'react';
import { I18n } from '@aws-amplify/core';
import { Row, Col, Switch, Input, Space } from 'antd';

import { ErrorModal } from './ErrorModal';
import { SuccessModal } from './SuccessModal';
import { BaseModal } from './shared/BaseModal';
import { GhostButton, PrimaryButton } from '@/components/ui/Button';
import { useDataExtractionReport } from '@/context/DataExtractionReport';
import { ACTION_PLAN_COLUMN_LABELS } from '@/components/Settings/DataExtraction/ExtractionType/Hierarchy/Steps/ActionPlans/constants';
import { useConfirmationModalHierarchy } from '@/components/Settings/DataExtraction/Content/hooks/useConfirmationModalHierarchy';
import { ExtendedDataExtractionData } from '@/components/Settings/DataExtraction/types';
import * as Typography from '@/components/Settings/DataExtraction/styles';
import type { ActionPlanColumnValue } from '@/components/Settings/DataExtraction/ExtractionType/Hierarchy/Steps/ActionPlans/types';
import * as S from './styles';

interface ConfirmationModalProps {
	visible: boolean;
	onCancel: () => void;
}

const actionPlanColumnLabels = ACTION_PLAN_COLUMN_LABELS;

export function ConfirmationModal({ visible, onCancel }: Readonly<ConfirmationModalProps>) {
	const {
		saveAsTemplate,
		templateName,
		showSuccessModal,
		showErrorModal,
		isConfirmButtonDisabled,
		handleSaveAsTemplateChange,
		handleTemplateNameChange,
		handleConfirm,
		handleSuccessModalClose,
		handleErrorModalClose,
		handleGoToHome
	} = useConfirmationModalHierarchy({ onCancel });

	const { dataExtractionReport, selectedReportNames } = useDataExtractionReport();
	const extractionName = dataExtractionReport?.extraction_name!;
	const extendedData = dataExtractionReport as ExtendedDataExtractionData;
	const selectedTools = extendedData?.data_extraction_tools || [];
	const selectedActionPlanColumns = extendedData?.data_extraction_reports_action_plan_columns?.columns || [];
	const hasActionPlans = Boolean(extendedData?.has_action_plan_extraction);

	const getReportTags = (): string[] => {
		const tags = new Set<string>();

		if (dataExtractionReport?.custom_report?.name) {
			const reportName = dataExtractionReport.custom_report.name;
			tags.add(`${I18n.get('Results')} ${reportName}`);
			if (hasActionPlans) tags.add(`${I18n.get('Action plans')} ${reportName}`);
		}

		(dataExtractionReport?.data_extraction_action_plan_origins || []).forEach((origin) => {
			tags.add(`${I18n.get('Action plans')} ${origin.custom_report_name}`);
		});

		if (!dataExtractionReport?.custom_report?.name && hasActionPlans && Array.isArray(selectedReportNames)) {
			selectedReportNames.forEach((name) => tags.add(`${I18n.get('Action plans')} ${name}`));
		}

		return Array.from(tags);
	};

	const reportTags = getReportTags();

	const footer = (
		<Row justify="center">
			<Col>
				<Space direction="horizontal" size={16}>
					<GhostButton onClick={onCancel}>{I18n.get('Cancel')}</GhostButton>
					<PrimaryButton type="primary" onClick={handleConfirm} disabled={isConfirmButtonDisabled}>
						{I18n.get('Confirm')}
					</PrimaryButton>
				</Space>
			</Col>
		</Row>
	);

	return (
		<>
			<BaseModal
				title={<S.CustomTitle>{extractionName}</S.CustomTitle>}
				visible={visible}
				onCancel={onCancel}
				width={600}
				footer={footer}
			>
				<Space direction="vertical" size={32} style={{ width: '100%' }}>
					<Row align="middle" justify="space-between" style={{ width: '100%' }}>
						<Col span={11}>
							<Space>
								<Switch checked={saveAsTemplate} onChange={handleSaveAsTemplateChange} />
								<Typography.LabelMediumRegular>
									{I18n.get('Set as template')}
								</Typography.LabelMediumRegular>
							</Space>
						</Col>
						<Col span={13}>
							<Input
								placeholder={I18n.get('Extraction template name')}
								value={templateName}
								onChange={handleTemplateNameChange}
								disabled={!saveAsTemplate}
								required={saveAsTemplate}
							/>
						</Col>
					</Row>
					<Space direction="vertical" size={16}>
						<Typography.LabelLargeRegular>
							{I18n.get('The extraction contains the following information:')}
						</Typography.LabelLargeRegular>
						<Typography.LabelLargeSemibold>{I18n.get('Content:')}</Typography.LabelLargeSemibold>

						<Space wrap>
							{reportTags.map((reportName) => (
								<S.ContentTag key={reportName}>
									<Typography.LabelMediumRegular>{reportName}</Typography.LabelMediumRegular>
								</S.ContentTag>
							))}

							{selectedTools.map((tool, index) => (
								<S.ContentTag key={index}>
									<Typography.LabelMediumRegular>{String(tool.label)}</Typography.LabelMediumRegular>
								</S.ContentTag>
							))}
						</Space>
					</Space>
					{selectedActionPlanColumns && selectedActionPlanColumns.length > 0 && (
						<Space direction="vertical" size={16}>
							<Typography.LabelLargeSemibold>
								{I18n.get('Content of action plans:')}
							</Typography.LabelLargeSemibold>

							<Space wrap>
								{selectedActionPlanColumns.map((column: any) => (
									<S.ContentTag key={column}>
										<Typography.LabelMediumRegular>
											{I18n.get(
												actionPlanColumnLabels[column as ActionPlanColumnValue] || column
											)}
										</Typography.LabelMediumRegular>
									</S.ContentTag>
								))}
							</Space>
						</Space>
					)}
				</Space>
			</BaseModal>

			<SuccessModal
				visible={showSuccessModal}
				onCancel={handleSuccessModalClose}
				extractionName={extractionName}
				onGoToHome={handleGoToHome}
			/>

			<ErrorModal visible={showErrorModal} onCancel={handleErrorModalClose} extractionName={extractionName} />
		</>
	);
}
